package com.vamvisions.main.core.presentation.markdown

/** Decides which link and image targets a document may use. Everything else is dropped or shown as text. */
object LinkPolicy {

    private val allowedLinkSchemes = setOf("http", "https", "mailto")
    private val schemePattern = Regex("^([A-Za-z][A-Za-z0-9+.-]*):")

    /**
     * Returns [href] when it is an `http`, `https` or `mailto` URL, or a site-relative path (`/...` but
     * not `//...`), and null otherwise. ASCII whitespace and control characters are removed first, the
     * way browsers do, so `java\tscript:` can't slip through.
     */
    fun sanitizeLink(href: String): String? {
        val cleaned = clean(href)
        if (isSiteRelative(cleaned)) return cleaned
        val scheme = schemePattern.find(cleaned)?.groupValues?.get(1)?.lowercase() ?: return null
        return cleaned.takeIf { scheme in allowedLinkSchemes }
    }

    /** Returns [src] when it is a site-relative path or an `https` URL, and null otherwise. */
    fun sanitizeImage(src: String): String? {
        val cleaned = clean(src)
        if (isSiteRelative(cleaned)) return cleaned
        val scheme = schemePattern.find(cleaned)?.groupValues?.get(1)?.lowercase() ?: return null
        return cleaned.takeIf { scheme == "https" }
    }

    private fun isSiteRelative(value: String) = value.startsWith("/") && !value.startsWith("//") && !value.startsWith("/\\")

    private fun clean(value: String) = value.filterNot { it.code <= 0x20 || it.code == 0x7F }
}
